import { useEffect, useRef, useState } from "react";
import { GridSetup, defaultGridSettings } from "./types";
import { DefaultHtmlAttributes } from "@syncfusion/ej2-react-base";
import { L10n } from "@syncfusion/ej2-base";
import {
  ActionEventArgs,
  AddEventArgs,
  Column,
  DeleteEventArgs,
  EditEventArgs,
  FailureEventArgs,
  FilterEventArgs,
  FilterSearchBeginEventArgs,
  GridComponent,
  GridModel,
  GroupEventArgs,
  PageEventArgs,
  RecordClickEventArgs,
  SaveEventArgs,
  SearchEventArgs,
  SortDescriptorModel,
  SortEventArgs,
  ToolbarItem,
  ToolbarItems,
} from "@syncfusion/ej2-react-grids";
import GridHeightSettings from "../GridSettings/GridHeightSettings";
import { useOData } from "../../../stores/OData/useOData";
import GridColumn from "../GridColumns/GridColumn";
import { Query } from "@syncfusion/ej2-data";
import { BaseGridModel } from "../GridTypes/BaseGridModel";
import GridRecordClickEventArgs from "../GridSettings/GridRecordClickEventArgs";
import { ClickEventArgs } from "@syncfusion/ej2-navigations";
import AppToasterV2 from "../../ToastV2/ToastV2";
import { GridPersistData } from "./types/GridPersistData";
import { GridFilterSettings } from "./types/GridFilterSettings";
import { ItemModel } from "@syncfusion/ej2-react-navigations";
import { ODataV4BeforeSendArgs } from "../../../stores/OData/types";

//Create custom Hook
export const useGrid = <T extends object>(gridSetup: GridSetup<T>) => {
  const {
    authRequestHeader,
    setupGridODataSource,
    baseActionCompleteEventHandler,
    baseActionFailureEventHandler,
  } = useOData({ getAuthRequestHeader: gridSetup.getAuthRequestHeader }, () => {
    setupGridSettings(gridSetup);
  });

  const [gridSettings, setGridSettings] = useState<
    GridModel & DefaultHtmlAttributes
  >();
  const [gridKey, setGridKey] = useState<string>();
  const [gridHeightSettings, setGridHeightSettings] =
    useState<GridHeightSettings>();
  const filterSettingsRef = useRef<GridFilterSettings>(undefined);
  const [filterSettings, setFilterSettings] = useState<GridFilterSettings>();
  const [defaultSort, setDefaultSort] = useState<SortDescriptorModel>();
  const [initialized, setInitialized] = useState<boolean>(false);
  const gridRef = useRef<GridComponent | null>(null);

  const [ready, setReady] = useState<boolean>(false);

  useEffect(() => {
    if (!!gridRef.current && !initialized) {
      initialize();
      setInitialized(true);
    }
  });

  useEffect(() => {
    if (gridSettings) {
      setReady(true);
    }
  }, [gridSettings]);

  // Computed
  const filtersCount =
    (filterSettings?.rawFilterSettings?.length ?? 0) +
    (filterSettings?.rawSortSettings?.length ?? 0);

  // Methods
  let requestType: string | undefined;
  const setRequestType = (type?: string) => {
    requestType = type;
  };
  let persistData: GridPersistData | undefined;
  const setPersistData = (data?: GridPersistData) => {
    persistData = data;
  };
  let requestFilterQuery: string | undefined;
  const setRequestFilterQuery = (filterQuery?: string) => {
    requestFilterQuery = filterQuery;
  };

  const clearFilters = (gridFilterSettings?: GridFilterSettings) => {
    if (!gridFilterSettings) {
      gridFilterSettings = getGridFilterSettings(gridRef.current);
    }

    // Clear  Filters (Direct, avoid errors)
    const fieldsToRemove = gridFilterSettings.rawFilterSettings?.map(
      (x) => x.field
    );
    if (gridFilterSettings.rawFilterSettings?.length > 0 && !!gridRef.current) {
      gridRef.current.filterSettings.columns =
        gridRef.current?.filterSettings.columns?.filter(
          (x) => !fieldsToRemove.includes(String(x.field))
        );

      gridRef.current?.setProperties({
        filterSettings: gridRef.current.filterSettings,
      });
    }

    // Clear Sorts
    if (gridFilterSettings.rawSortSettings?.length > 0) {
      gridFilterSettings.rawSortSettings.forEach((col) => {
        gridRef.current?.removeSortColumn(String(col.field));
      });
    }

    if (gridRef.current?.headerModule) {
      gridRef.current?.refreshHeader();
    }
  };

  const removeSort = (field: string) => {
    clearFilters({
      rawSortSettings: [{ field: field }],
    } as GridFilterSettings);
  };

  const removeFilter = (field: string) => {
    clearFilters({
      rawFilterSettings: [{ field: field }],
    } as GridFilterSettings);
  };

  const initializeAutoHeight = () => {
    // Inner Function
    const autoHeight = () => {
      if (gridRef.current) {
        gridRef.current.height =
          window.innerHeight - (gridHeightSettings?.autoHeightOffset ?? 250);
      }
    };

    // Init
    autoHeight();

    // On Resize
    window.addEventListener("resize", () => {
      autoHeight();
    });
  };

  const initialize = () => {
    storeFilterSettings();
    initializeAutoHeight();
  };

  const getGridFilterSettings = (
    scopeGridInstance: GridComponent | null
  ): GridFilterSettings => {
    if (!persistData) {
      getPersistData();
    }
    const gridFilterSettings = {
      rawQuery: requestFilterQuery,
      rawSearch: scopeGridInstance?.searchSettings.key,
      rawColumnSettings: (scopeGridInstance?.columns as Column[]).filter(
        (x) => x.visible
      ),
      rawFilterSettings: persistData?.filterSettings.columns || [],
      rawSortSettings: persistData?.sortSettings.columns || [],
      columnNames: (scopeGridInstance?.columns as Column[])
        .filter((x) => x.visible)
        ?.map((x) => x.field)
        ?.join(","),
      gridKey: gridSetup.gridKey,
    } as GridFilterSettings;

    return gridFilterSettings;
  };

  const getPersistData = () => {
    if (!gridRef.current) {
      return;
    }

    // Set Persist Data
    const persistData = JSON.parse(
      gridRef.current?.getPersistData()
    ) as GridPersistData;

    setPersistData(persistData);
  };

  const storeFilterSettings = (request?: string) => {
    if (!gridRef.current) {
      return;
    }

    if (
      !request ||
      ["sorting", "filtering", "searching", "reorder", "refresh"].includes(
        String(request)
      )
    ) {
      getPersistData();

      // Set Filter Settings
      const settings = getGridFilterSettings(gridRef.current);
      if (settings) {
        setFilterSettings({ ...settings });
        filterSettingsRef.current = settings;
      }
    }
  };

  // Setup
  const setupGridSettingsColumns = (
    columnsConfiguration: Readonly<GridColumn[]>
  ) => {
    const gridColumnSettings = columnsConfiguration.map((colsConfig) => ({
      ...{ textAlign: "Left", displayAsCheckBox: false },
      ...colsConfig,
    })) as GridColumn[];

    return gridColumnSettings;
  };

  const setupGridSettingsQuery = (
    columnsConfiguration: Readonly<GridColumn[]>
  ) => {
    const query = new Query();

    const columnsToFetch = columnsConfiguration
      .filter((x) => !x.isComputed)
      .map((x) => x.field);
    const alwaysSelectColumns =
      gridSetup?.gridConfiguration?.alwaysSelectColumns ?? [];

    query?.select([...alwaysSelectColumns, ...columnsToFetch]);

    // Add params for Grid Views
    query?.addParams("gridId", gridSetup.gridKey);

    if (gridSetup.gridViewTitle) {
      query?.addParams(
        "gridViewTitle",
        encodeURIComponent(gridSetup.gridViewTitle)
      );
    }

    // Add custom params from setup
    if (gridSetup.dataParams) {
      for (const key in gridSetup.dataParams) {
        query?.addParams(key, gridSetup.dataParams[key]);
      }
    }

    if (gridSetup.predicate) {
      query?.where(gridSetup.predicate);
    }

    const setupDefaultSort = gridSetup.gridConfiguration?.defaultSort;
    if (setupDefaultSort && setupDefaultSort.field) {
      if (setupDefaultSort.direction === "Ascending") {
        query.sortBy(setupDefaultSort.field);
      } else {
        query.sortByDesc(setupDefaultSort.field);
      }
    }

    return query;
  };

  const setupRecordClickEvent = <T extends object>(
    gridConfiguration?: Readonly<BaseGridModel<T>>
  ) => {
    return (x: RecordClickEventArgs) => {
      // Check if the clicked column has the no-clickable custom attribute
      const clickedColumn = x.column;

      if (
        clickedColumn &&
        clickedColumn.customAttributes &&
        clickedColumn.customAttributes["no-clickable"]
      ) {
        // Don't trigger onRecordClick for columns marked as no-clickable
        return;
      }

      if (gridConfiguration?.recordClick) {
        gridConfiguration.recordClick(x);
      }

      if (gridConfiguration?.onRecordClick) {
        const recordClickEventArgs = {
          ...x,
        } as GridRecordClickEventArgs<T>;
        gridConfiguration.onRecordClick(recordClickEventArgs);
      }
    };
  };

  const setupToolbarClickEvent = <T extends object>(
    gridConfiguration?: Readonly<BaseGridModel<T>>
  ) => {
    return (clickEventArgs: ClickEventArgs) => {
      if (gridConfiguration?.toolbarClick) {
        gridConfiguration.toolbarClick(clickEventArgs);
      }

      if (clickEventArgs.item.id === "ExcelExport") {
        AppToasterV2.showDownloadingFileAppToaster("Excel file");
        gridRef.current
          ?.excelExport({
            fileName: gridConfiguration?.exportFilename ?? "Export.xlsx",
          })
          .then(() => {
            AppToasterV2.showSuccessAppToaster("Excel download finished");
          });
      }
    };
  };

  const setupDataboundEvent = <T extends object>(
    gridConfiguration?: Readonly<BaseGridModel<T>>
  ) => {
    return (obj: Object) => {
      if (gridConfiguration?.dataBound) {
        gridConfiguration.dataBound(obj);
      }

      gridRef.current?.autoFitColumns(
        (gridRef.current?.columns as Column[])
          .filter((x) =>
            // only autofit columns whose width is set at its default
            typeof x.width === "number" ? x.width === 200 : true
          )
          .map((x) => x.foreignKeyField)
      );
    };
  };

  const setupCreatedEvent = <T extends object>(
    gridConfiguration?: Readonly<BaseGridModel<T>>
  ) => {
    return (obj: Object) => {
      if (gridConfiguration?.created) {
        gridConfiguration.created(obj);
      }

      storeFilterSettings();
    };
  };

  const setupActionBeginEvent = <T extends object>(
    gridConfiguration?: Readonly<BaseGridModel<T>>
  ) => {
    return (
      eventArgs:
        | PageEventArgs
        | GroupEventArgs
        | FilterEventArgs
        | SearchEventArgs
        | SortEventArgs
        | AddEventArgs
        | SaveEventArgs
        | EditEventArgs
        | DeleteEventArgs
        | ActionEventArgs
        | FilterSearchBeginEventArgs
    ) => {
      if (gridConfiguration?.actionBegin) {
        gridConfiguration.actionBegin(eventArgs);
      }
      setRequestType(eventArgs.requestType);

      const filterChoiceArgs = eventArgs as FilterSearchBeginEventArgs;
      // Set Filter Choice Count
      if (eventArgs.requestType === "filterchoicerequest") {
        filterChoiceArgs.filterChoiceCount =
          gridConfiguration?.filterChoiceMaxCount ?? 9999;

        if (gridConfiguration?.fixODataQuestionMark) {
          // when we apply groupBy query, we have to fix default sorting to do not try to order by a column that is not part of groupBy statement.

          const obj = filterChoiceArgs.query;

          const groupByKeys = obj.distincts ?? [];
          if (!groupByKeys.length) {
            return;
          }
          // get onSortBy
          const sortIdx = obj.queries.findIndex((q) => q.fn === "onSortBy");
          if (sortIdx === -1) {
            return; // no sorting applied - nothing to fix
          }

          const sortOptions = obj.queries[sortIdx].e ?? {};
          const currentSortingColumn: string | undefined =
            sortOptions.fieldName;
          const direction = sortOptions.direction ?? "ascending";

          // current active sorting column aligns with groupBy - this is ok.
          if (
            currentSortingColumn &&
            groupByKeys.includes(currentSortingColumn)
          ) {
            return;
          }

          // get first group by key
          const groupByKey = groupByKeys[0];

          obj.queries[sortIdx] = {
            fn: "onSortBy",
            e: { fieldName: groupByKey, direction },
          };
        }
      }
    };
  };

  const setupActionFailureEvent = <T extends object>(
    gridConfiguration?: Readonly<BaseGridModel<T>>
  ) => {
    return async (eventArgs: FailureEventArgs) => {
      if (gridConfiguration?.actionFailure) {
        gridConfiguration.actionFailure(eventArgs);
      }
      // Process Error
      await baseActionFailureEventHandler(eventArgs, () =>
        gridRef.current?.refresh()
      );
    };
  };

  const setupActionCompleteEvent = <T extends object>(
    gridConfiguration?: Readonly<BaseGridModel<T>>
  ) => {
    return (
      eventArgs:
        | PageEventArgs
        | GroupEventArgs
        | FilterEventArgs
        | SearchEventArgs
        | SortEventArgs
        | AddEventArgs
        | SaveEventArgs
        | EditEventArgs
        | DeleteEventArgs
        | ActionEventArgs
    ) => {
      if (gridConfiguration?.actionComplete) {
        gridConfiguration.actionComplete(eventArgs);
      }

      baseActionCompleteEventHandler();
    };
  };

  const onBeforeSend = ({
    queryString,
  }: ODataV4BeforeSendArgs): [string, Record<string, string> | undefined] => {
    setRequestFilterQuery(queryString);

    if (requestType) {
      storeFilterSettings(requestType);
    }

    // Initial Sorting
    if (defaultSort && !queryString.includes("$orderby")) {
      queryString =
        queryString +
        `&$orderby=${defaultSort.field}%20${
          defaultSort.direction === "Ascending" ? "asc" : "desc"
        }`;
    }

    // Handle conflict between User and Default sort
    const setupDefaultSort = gridSetup.gridConfiguration?.defaultSort;
    if (setupDefaultSort && setupDefaultSort.field) {
      // Matches when query contains duplicate orderby parameters on the default sort column
      // Captures the interior of the orderby parameter
      // e.g.: $orderby=createdAt%20desc,createdAt => createdAt%20desc,createdAt
      const doubleSortRe = new RegExp(
        `\\$orderby=(${setupDefaultSort.field}(?:%20desc)?,${setupDefaultSort.field}(?:%20desc)?)`
      );

      // @ts-ignore
      const doubleSortCapture = queryString.match(doubleSortRe)?.at(1);
      if (doubleSortCapture) {
        // Remove the default sort column from the orderby parameter to resolve the conflict
        queryString = queryString.replace(
          doubleSortRe,
          `$orderby=${doubleSortCapture.split(",")[0]}`
        );
      }
    }

    // Grouping Filters missing Filter
    if (
      queryString.includes("$apply=groupby") &&
      !queryString.includes("$filter")
    ) {
      const params = new URLSearchParams(filterSettingsRef.current?.rawQuery);
      if (params.has("$filter")) {
        queryString =
          queryString +
          `&$filter=${encodeURIComponent(params.get("$filter") as string)}`;
      }
    }

    if (gridSetup.gridConfiguration?.fixODataQuestionMark) {
      queryString = queryString.replace(/^\?/, "?fix=1&");
    }

    if (gridSetup.gridConfiguration?.removeSelect) {
      queryString = queryString
        .split("&")
        .filter((x) => !x.startsWith("$select"))
        .join("&");
    }

    return [queryString, authRequestHeader];
  };

  // SETUP GRID SETTINGS
  const setupGridSettings = (gridSetup: GridSetup<T>) => {
    setGridKey(gridSetup.gridKey);

    const gridHeight = gridSetup.gridHeightSettings ?? {
      height: 600,
      autoHeightOffset: 250,
    };
    setGridHeightSettings(gridSetup.gridHeightSettings ?? gridHeight);

    const settings = {
      ...defaultGridSettings,
      ...gridSetup.gridConfiguration,
    };

    // Overwrite Search
    if (gridSetup.allowSearch) {
      if (
        !settings.toolbar?.find(
          (x: ToolbarItems | string | ItemModel | ToolbarItem) =>
            typeof x !== "string" && (x as ItemModel)?.text === "Search"
        )
      )
        settings.toolbar?.push({
          text: "Search",
        });
    } else {
      settings.toolbar = settings.toolbar?.filter(
        (x: ToolbarItems | string | ItemModel | ToolbarItem) =>
          typeof x !== "string" && (x as ItemModel)?.text !== "Search"
      );
    }

    // Override Excel Export
    if (settings.allowExcelExport) {
      if (
        !settings.toolbar?.find(
          (x: ToolbarItems | string | ItemModel | ToolbarItem) =>
            typeof x !== "string" && (x as ItemModel)?.id === "ExcelExport"
        )
      )
        settings.toolbar?.push({
          text: "Export to Excel",
          tooltipText: "Export to Excel",
          prefixIcon: "e-upload-2",
          id: "ExcelExport",
        });
    } else {
      settings.toolbar = settings.toolbar?.filter(
        (x: ToolbarItems | string | ItemModel | ToolbarItem) =>
          typeof x !== "string" && (x as ItemModel)?.id !== "ExcelExport"
      );
    }

    settings.height = gridHeight.height;

    // Update Filter and Sort Settings
    settings.filterSettings = {
      ...settings.filterSettings,
      ...{ columns: gridSetup.filterConfiguration },
    };

    settings.sortSettings = {
      ...settings.sortSettings,
      ...{
        columns: gridSetup.sortConfiguration ?? [],
      },
    };

    // Setup Properties
    settings.columns = setupGridSettingsColumns(gridSetup.columnsConfiguration);
    if (!!gridSetup.setupActions) {
      settings.columns = gridSetup.setupActions(
        settings.columns as GridColumn[]
      );
    }

    settings.query = setupGridSettingsQuery(gridSetup.columnsConfiguration);
    settings.dataSource = setupGridODataSource(
      gridSetup.dataSource,
      gridSetup.columnsConfiguration as GridColumn[],
      onBeforeSend
    );

    // Setup Events
    settings.recordClick = setupRecordClickEvent(gridSetup.gridConfiguration);
    settings.toolbarClick = setupToolbarClickEvent(gridSetup.gridConfiguration);
    settings.dataBound = setupDataboundEvent(gridSetup.gridConfiguration);
    settings.created = setupCreatedEvent(gridSetup.gridConfiguration);
    settings.actionBegin = setupActionBeginEvent(gridSetup.gridConfiguration);
    settings.actionFailure = setupActionFailureEvent(
      gridSetup.gridConfiguration
    );
    settings.actionComplete = setupActionCompleteEvent(
      gridSetup.gridConfiguration
    );

    // Set default sort
    setDefaultSort(gridSetup.gridConfiguration?.defaultSort);

    // Update locale for boolean values
    L10n.load({
      "en-US": {
        grid: {
          True: "Yes",
          False: "No",
        },
      },
    });

    // Return Grid Model

    setGridSettings({ ...settings });
  };

  return {
    ready,
    gridKey,
    gridRef,
    gridSettings,
    filtersCount,
    filterSettings,
    removeFilter,
    clearFilters,
    removeSort,
  };
};
